import {
  Edit as EditIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Iconify from 'src/components/Iconify';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { TaskWrapper, WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import {
  useDocumentReference,
  useQuestionnaireResponse,
  useTask,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { Option, onSuccess } from 'src/@types/crs/case';
import { TaskStatusValue } from 'src/@types/crs/task';
import CustomDrawer, { CustomDrawerProps } from 'src/components/CustomDrawer';
import DialogCloseButton from 'src/components/DialogCloseButton';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { updateTask } from '../main/task.api';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { spreadSxProp } from 'src/utils/cssStyles';
import { getValueX } from 'src/utils/fhir';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import ExpandableCard from 'src/components/common/ExpandableCard';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import {
  taskToAnnotations,
  ANNOTATION_PREVIEW_LIMIT,
  ANNOTATION_TRUNCATE_LENGTH,
} from 'src/careflow/care-coordination/case/detail/components/task-annotation-utils';
import { getFormattedDate as getAnnotationFormattedDate } from 'src/careflow/common/annotation-utils';
import TaskAnnotationsPanel from 'src/careflow/care-coordination/case/detail/components/TaskAnnotationsPanel';
import { isValidURL } from 'src/utils/url';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { capitalize } from 'src/utils/string';
import BadgeBox from 'src/careflow/common/BadgeBox';
import { getSeverityDueDateData } from 'src/careflow/helpers/getSeverityDueDateData';
import TaskStatusSeverity from '../main/TaskStatusSeverity';
import ServiceRequestDetailsContainer from 'src/careflow/care-coordination/case/detail/components/ServiceRequestDetailsContainer';
import { itsNatTask } from 'src/careflow/inck/nat-task/helpers/task';
import {
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { DocumentReferenceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import { getReferenceResourceId, getReferenceResourceType } from 'src/careflow/helpers/common';
import Viewer from '../../patient/assessment/viewer/Viewer';

// Get reference string from task output (valueReference or valueCodeableReference).
function getOutputReference(output: Record<string, any>): string | undefined {
  const ref = output?.valueReference?.reference ?? output?.valueCodeableReference?.reference;
  return typeof ref === 'string' ? ref : undefined;
}

// Get display value for task output; prefers reference/display for references.
function getOutputDisplayValue(
  output: Record<string, any>,
  valueX: { value?: string } | undefined
): string {
  const ref = getOutputReference(output);
  if (ref) return ref;
  const display = output?.valueReference?.display ?? output?.valueCodeableReference?.display;
  if (typeof display === 'string') return display;
  return valueX?.value ?? '';
}

function getAnswerDisplayValue(a: QuestionnaireResponseItemAnswer): string {
  if (a.valueString !== undefined) return a.valueString;
  if (a.valueCoding?.display) return a.valueCoding.display;
  if (a.valueInteger !== undefined) return String(a.valueInteger);
  if (a.valueBoolean !== undefined) return String(a.valueBoolean);
  if (a.valueDate) return a.valueDate;
  if (a.valueDateTime) return a.valueDateTime;
  if (a.valueUri) return a.valueUri;
  if (a.valueReference?.display) return a.valueReference.display;
  if (a.valueReference?.reference) return a.valueReference.reference;
  return '';
}

function QuestionnaireResponsePreviewContent({ qr }: { qr: QuestionnaireResponse }) {
  const renderItem = (item: QuestionnaireResponseItem, depth = 0): React.ReactNode => {
    const answers = item.answer?.map((a) => getAnswerDisplayValue(a)).filter(Boolean) ?? [];
    const answerText = answers.join(', ');
    return (
      <Box key={item.linkId} sx={{ pl: depth * 2, mb: 1.5 }}>
        {item.text && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {item.text}
          </Typography>
        )}
        {answerText && (
          <Typography variant="body2" sx={{ color: 'text.primary', ml: 1 }}>
            {answerText}
          </Typography>
        )}
        {item.item?.map((child) => renderItem(child, depth + 1))}
      </Box>
    );
  };
  return <Box>{qr.item?.map((item) => renderItem(item))}</Box>;
}

interface TaskDetailDrawerProps extends Omit<CustomDrawerProps, 'title' | 'anchor'> {
  taskExternal?: WrappedTask | null;
  goal?: WrappedGoal | null;
  onEdit: (task: WrappedTask) => void;
  onSuccesfulEdit: onSuccess;
  taskId?: string;
  isAllowedToEdit: boolean;
  title?: string;
}

const { valueTextFontSize, blackTextColor, margin5 }: { [k: string]: SxProps } = {
  valueTextFontSize: {
    fontSize: '0.8rem',
  },
  blackTextColor: {
    color: '#212b36',
  },
  margin5: {
    marginRight: '5px',
  },
};

export const getFormattedDate = (date: moment.Moment): string => {
  const { formattedDueDate } = getSeverityDueDateData(date);
  if (formattedDueDate === 'N/A') return '';
  return formattedDueDate;
};

export const Container = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => (
  <Box
    sx={[
      {
        px: 2.8,
      },
      ...spreadSxProp(sx),
    ]}
  >
    {children}
  </Box>
);

// Styled Section Container
const SectionContainer = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => (
  <Paper
    elevation={0}
    sx={[
      {
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.lighter',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
      ...spreadSxProp(sx),
    ]}
  >
    {children}
  </Paper>
);

// Define Section component
interface SectionProps {
  title: string;
  children: React.ReactNode;
  sx?: SxProps;
}

export const Section = ({ title, children, sx }: SectionProps) => (
  <Box sx={[{ my: 2 }, ...spreadSxProp(sx)]}>
    {title && (
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          mb: 2.5,
          color: 'text.primary',
          fontSize: '0.95rem',
          letterSpacing: '0.02em',
        }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

type LabelValueKeys = Option<string | React.ReactNode>;

export interface LabelValueProps extends LabelValueKeys {
  labelSx?: SxProps;
}

export const LabelValue = ({ label, value, labelSx }: LabelValueProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      py: 1.5,
      px: 0,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:first-child': {
        borderBottom: 'none',
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    }}
  >
    <Typography
      sx={[
        valueTextFontSize,
        {
          flex: 1,
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        ...spreadSxProp(labelSx),
      ]}
    >
      {label}
    </Typography>
    <Typography
      sx={[
        valueTextFontSize,
        {
          flex: 2,
          color: 'text.primary',
          fontWeight: 400,
          fontSize: '0.875rem',
          textAlign: 'right',
          wordBreak: 'break-word',
        },
      ]}
    >
      {value || '-'}
    </Typography>
  </Box>
);

const LabelLink = ({
  label,
  value,
  labelSx,
}: {
  label: string;
  value?: string;
  labelSx?: SxProps;
}) => (
  <Box
    sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', my: 2, margin5 }}
  >
    <Typography sx={[valueTextFontSize, { flex: 1, color: '#637381' }, ...spreadSxProp(labelSx)]}>
      {label}
    </Typography>
    <Link sx={[valueTextFontSize, blackTextColor, { flex: 2 }]} target="_blank" href={value}>
      {value}
    </Link>
  </Box>
);

const TaskDetailDrawer = ({
  taskExternal,
  taskId: taskIdProp,
  goal,
  onEdit,
  onSuccesfulEdit,
  isAllowedToEdit,
  title,
  ...props
}: TaskDetailDrawerProps) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(taskExternal?.status ?? '');
  const [isEditMode, setIsEditMode] = useState(false);
  const [verifiedNatTaskBoolean, setVerifiedNatTaskBoolean] = useState(false);
  const [isAnnotationsPanelOpen, setIsAnnotationsPanelOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewQuestionnaireResponseId, setPreviewQuestionnaireResponseId] = useState<
    string | null
  >(null);

  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();
  const { configurations, componentsData } = useTenantConfigData();

  const [previewDocument, { isFetching: isPreviewDocumentLoading }] = useDocumentReference(
    previewDocumentId ?? '',
    { autofetch: !!previewDocumentId, map: DocumentReferenceWrapper }
  );

  const [previewQuestionnaireResponse, { isFetching: isPreviewQRLoading }] =
    useQuestionnaireResponse(previewQuestionnaireResponseId ?? '', {
      autofetch: !!previewQuestionnaireResponseId,
    });

  const { activityDefintionsDisabledStatus = null } = componentsData?.task ?? {};

  const [taskRecord, { isFetching: isTaskLoading }] = useTask(taskIdProp, {
    map: TaskWrapper,
    autofetch: !!taskIdProp,
  });
  const {
    valueSets: [taskStatuses, taskReasons],
  } = useValueSetsByIdentifiers(['ph-task-status', 'task-reason']);

  const currentUserName = useAuth()?.getCurrentUser?.()?.name ?? '';

  const task = useMemo(() => {
    const raw = taskExternal ?? taskRecord ?? null;
    if (!raw) return null;
    if (typeof (raw as any)?.getOwnerDisplay === 'function') return raw as WrappedTask;
    return TaskWrapper(raw) as WrappedTask;
  }, [taskRecord, taskExternal]);

  const disabledStatus = useMemo(() => {
    if (!task?.instantiatesCanonical || !activityDefintionsDisabledStatus) return false;
    const disabled =
      task?.instantiatesCanonical && task?.isActive()
        ? activityDefintionsDisabledStatus?.includes(task.instantiatesCanonical)
        : false;
    return disabled;
  }, [task, activityDefintionsDisabledStatus]);

  const [goalToDisplay, setGoalToDisplay] = useState<WrappedGoal | null>(null);

  useEffect(() => {
    setSelectedStatus(task?.status ?? '');
    setIsEditMode(false);
    if (task) {
      const verifiedNatTask = itsNatTask(task);
      setVerifiedNatTaskBoolean(verifiedNatTask);
    }
  }, [task]);

  useEffect(() => {
    if (task) {
      const { goalTaskAssociations } = window as any;

      if (goalTaskAssociations && task.id) {
        const associatedGoal = goalTaskAssociations[task.id];
        if (associatedGoal) {
          setGoalToDisplay(associatedGoal);
        } else {
          setGoalToDisplay(null);
        }
      } else {
        setGoalToDisplay(null);
      }
    } else {
      setGoalToDisplay(null);
    }
  }, [task]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setSelectedStatus(event.target.value as string);
  };
  const toggleEditMode = () => {
    setIsEditMode((isEditMode) => !isEditMode);
  };

  const handleTaskUpdate = async () => {
    const response = await updateTask({ status: selectedStatus }, task?.id as string);
    if (!response) {
      enqueueSnackbar('Unable to update Task Status. Please Try Again', {
        variant: 'error',
      });
      return;
    }
    onSuccesfulEdit(response);
  };

  const getTaskNumber = useMemo(
    () =>
      task
        ? task?.getInternalNumber?.(configurations?.fhir?.taskInternalNumberIdentifierSystem) ??
          task?.getTaskNumberNAT(configurations?.fhir?.taskNATIdenitifierType) ??
          ''
        : '',
    [task, configurations]
  );

  // General Details
  const generalDetails: LabelValueProps[] = [
    { label: 'No.', value: getTaskNumber },
    { label: 'Activity Type', value: task?.getTaskTypeDisplay() ?? task?.getTaskType() ?? '' },
    ...(task?.getForDisplay()
      ? [{ label: 'Patient', value: task?.getForDisplay() as string }]
      : []),
    { label: 'Created On', value: getFormattedDate(moment(task?.authoredOn ?? null)) },
    { label: 'Last Modified Date', value: getFormattedDate(moment(task?.lastModified ?? null)) },
    { label: 'Start Date', value: getFormattedDate(moment(task?.getStartDate() ?? null)) },
    { label: 'End Date', value: getFormattedDate(moment(task?.getEndDate() ?? null)) },
    { label: 'Status', value: '' },
    { label: 'Priority', value: capitalize(task?.priority ?? '') },
    { label: 'Owner', value: task?.getOwnerDisplay() ?? task?.getOwnerId() ?? '' },
  ];

  const basedOnServiceRequestId = useMemo(() => {
    if (!task?.basedOn) {
      return undefined;
    }

    const item = task.basedOn.find((item) => item.reference?.includes('ServiceRequest/'));

    if (item) {
      const splitRef = item.reference?.split('/');

      if (splitRef) {
        return splitRef[splitRef?.length - 1];
      }
    }

    return undefined;
  }, [task]);

  const getRequestedStartDate = useMemo(() => {
    if (!task) return null;
    const parentExtension = task?.extension?.find(
      (ext) =>
        ext?.url ===
        'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-detail'
    );
    const nestedRequestedPeriod = parentExtension?.extension?.find(
      (ext) => ext?.url === 'requestedPeriod'
    );
    const requestedStartDate = nestedRequestedPeriod?.valuePeriod?.start;
    return requestedStartDate ? moment(requestedStartDate) : null;
  }, [task]);

  const getRequestedEndDate = useMemo(() => {
    if (!task) return null;
    const dueDate = task?.getDueDate();
    return dueDate ? moment(dueDate) : null;
  }, [task]);

  const taskDetails: LabelValueProps[] = [
    { label: 'No.', value: getTaskNumber },
    { label: 'Activity Type', value: task?.getTaskTypeDisplay() ?? task?.getTaskType() ?? '' },
    ...(task?.getForDisplay()
      ? [{ label: 'Patient', value: task?.getForDisplay() as string }]
      : []),
    { label: 'Created On', value: getFormattedDate(moment(task?.authoredOn ?? null)) },
    { label: 'Last Modified Date', value: getFormattedDate(moment(task?.lastModified ?? null)) },
    { label: 'Start Date', value: getFormattedDate(getRequestedStartDate ?? moment(null)) },
    { label: 'End Date', value: getFormattedDate(getRequestedEndDate ?? moment(null)) },
    { label: 'Status', value: '' },
    { label: 'Priority', value: capitalize(task?.priority ?? '') },
    { label: 'Reason', value: task?.getReasonDisplay() ?? task?.getReasonCode() ?? '' },
    { label: 'Owner', value: task?.getOwnerDisplay() ?? task?.getOwnerId() ?? '' },
    { label: 'Description', value: task?.description ?? '' },
    // { label: 'Comments', value: task?.getComments() ?? '' },
  ];

  useEffect(() => {
    if (!props?.open) setIsEditMode(false);
  }, [props?.open]);

  return (
    <CustomDrawer
      {...props}
      title={'Task Details'}
      anchor="right"
      containerSx={{ width: '25vw' }}
      contentContainerSx={{ padding: 2.8, backgroundColor: 'grey.50' }}
    >
      {!isTaskLoading && task ? (
        <>
          {!!goalToDisplay && (
            <SectionContainer>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary',
                  fontSize: '0.95rem',
                  letterSpacing: '0.02em',
                }}
              >
                Goal
              </Typography>
              <Typography
                sx={[
                  valueTextFontSize,
                  {
                    lineHeight: 1.6,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'primary.lighter',
                    color: 'primary.darker',
                  },
                ]}
              >
                {goalToDisplay?.getPlainDescription?.()}
              </Typography>
            </SectionContainer>
          )}

          {/* General Section */}
          <SectionContainer>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2.5,
                color: 'text.primary',
                fontSize: '0.95rem',
                letterSpacing: '0.02em',
              }}
            >
              General
            </Typography>
            {generalDetails.map((taskDetail) => (
              <React.Fragment key={taskDetail.label}>
                {taskDetail?.label === 'Status' ? (
                  <LabelValue
                    label={taskDetail.label}
                    labelSx={{ alignSelf: 'center' }}
                    value={
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                      >
                        {!isEditMode ? (
                          <TaskStatusSeverity
                            taskStatuses={taskStatuses?.asList?.() ?? []}
                            status={selectedStatus as TaskStatusValue}
                            sx={{ width: '60%' }}
                          />
                        ) : (
                          <Select
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            sx={{ height: 30 }}
                          >
                            {taskStatuses?.asList?.()?.map?.((option) => (
                              <MenuItem key={option.code} value={option.code}>
                                {option.display}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                        <IconButton
                          disabled={
                            verifiedNatTaskBoolean
                              ? !checkAclValidation({ acls: [crsAcls.NAT.TASK.EDIT] })
                              : disabledStatus
                              ? disabledStatus
                              : !isAllowedToEdit
                          }
                          onClick={() => {
                            if (isEditMode) {
                              handleTaskUpdate();
                            }
                            toggleEditMode();
                          }}
                        >
                          {isEditMode ? (
                            <SaveIcon htmlColor="#00ab55" />
                          ) : (
                            <EditIcon htmlColor="#919eab" />
                          )}
                        </IconButton>
                      </Box>
                    }
                  />
                ) : (
                  <LabelValue {...taskDetail} />
                )}
              </React.Fragment>
            ))}
            {/* Display task inputs using BadgeBox component - shows Input 1, Input 2, etc. with Type and Value fields */}
            {task?.input && task.input.length > 0 && (
              <>
                {task.input.map((item, index) => {
                  // Extract type display name or code from FHIR coding structure
                  const valueType =
                    item?.type?.coding?.[0]?.display ?? item?.type?.coding?.[0]?.code ?? '';
                  // Extract value using utility function that handles various FHIR value types (valueString, valueUrl, etc.)
                  const valueValue = getValueX(item);
                  const itemsBadge = [
                    { label: 'Type', value: valueType },
                    { label: 'Value', value: valueValue?.value ?? '' },
                  ];

                  return (
                    <BadgeBox
                      key={`input-bbox-${index}`}
                      index={index}
                      items={itemsBadge}
                      blackTextColor={blackTextColor}
                      valueTextFontSize={valueTextFontSize}
                      titleBadge={'Input'}
                    />
                  );
                })}
              </>
            )}
            {/* Display task outputs using BadgeBox component - shows Output 1, Output 2, etc. with Type and Value fields */}
            {task?.output && task.output.length > 0 && (
              <>
                {task.output.map((item, index) => {
                  // Extract type display name or code from FHIR coding structure
                  const valueType =
                    item?.type?.coding?.[0]?.display ?? item?.type?.coding?.[0]?.code;
                  const valueValue = getValueX(item);
                  const displayValue = getOutputDisplayValue(item, valueValue);
                  const outputRef = getOutputReference(item);
                  const refType = outputRef ? getReferenceResourceType(outputRef) : '';
                  const refId = outputRef ? getReferenceResourceId(outputRef) : '';
                  const isDocumentReference = refType === 'DocumentReference';
                  const outputValueUri =
                    typeof item?.valueUri === 'string' ? item.valueUri : undefined;
                  const hasOutputUrl = !!outputValueUri && isValidURL(outputValueUri);
                  const isCarePlanRef = refType === 'CarePlan' && refId;
                  const isMeasureReportRef =
                    refType === 'MeasureReport' && refId && task?.id && itsNatTask(task);
                  const isQuestionnaireResponseRef = refType === 'QuestionnaireResponse' && refId;
                  const isServiceRequestRef = refType === 'ServiceRequest' && refId;
                  const fhirBaseUrl = configurations?.fhir?.baseUrl;
                  const isOtherResourceRef =
                    !!outputRef &&
                    !!refId &&
                    !!refType &&
                    !!fhirBaseUrl &&
                    ![
                      'DocumentReference',
                      'CarePlan',
                      'MeasureReport',
                      'QuestionnaireResponse',
                      'ServiceRequest',
                    ].includes(refType);
                  const itemsBadge = [
                    { label: 'Type', value: valueType },
                    { label: 'Value', value: displayValue },
                  ];

                  const outputStackSx = {
                    border: '1px solid',
                    borderColor: '#919eab3d',
                    borderRadius: 1,
                    padding: '8px',
                  };

                  if (isDocumentReference && refId) {
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? ''}
                          </Typography>
                          <Tooltip title={i18n('selectAttachment.previewDocument', 'crs')}>
                            <IconButton size="small" onClick={() => setPreviewDocumentId(refId)}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Iconify icon="carbon:view" sx={{ width: 18, height: 18 }} />
                                <Iconify icon="eva:file-fill" sx={{ width: 18, height: 18 }} />
                              </Box>
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue ?? ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  if (hasOutputUrl) {
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? ''}
                          </Typography>
                          <Tooltip title="View link">
                            <IconButton
                              size="small"
                              onClick={() => window.open(outputValueUri, '_blank')}
                            >
                              <Iconify icon="eva:globe-outline" sx={{ width: 18, height: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue || outputValueUri || ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  if (isCarePlanRef) {
                    const caseHref = `${PATH_DASHBOARD.crs.case}/${refId}`;
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? 'Care Plan'}
                          </Typography>
                          <Tooltip title="View case">
                            <IconButton
                              size="small"
                              onClick={() => window.open(caseHref, '_blank')}
                            >
                              <Iconify
                                icon="eva:external-link-outline"
                                sx={{ width: 18, height: 18 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue ?? outputRef ?? ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  if (isMeasureReportRef) {
                    const natTaskHref = `${PATH_DASHBOARD.nat.task}/${task?.id}`;
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? 'Measure Report'}
                          </Typography>
                          <Tooltip title="View NAT task">
                            <IconButton
                              size="small"
                              onClick={() => window.open(natTaskHref, '_blank')}
                            >
                              <Iconify
                                icon="eva:external-link-outline"
                                sx={{ width: 18, height: 18 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue ?? outputRef ?? ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  if (isQuestionnaireResponseRef) {
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? 'Questionnaire Response'}
                          </Typography>
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={() => setPreviewQuestionnaireResponseId(refId)}
                            >
                              <Iconify icon="carbon:view" sx={{ width: 18, height: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue ?? outputRef ?? ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  if (isServiceRequestRef) {
                    const referralHref = `${PATH_DASHBOARD.crs.referral}/${refId}`;
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? 'Service Request'}
                          </Typography>
                          <Tooltip title="View referral">
                            <IconButton
                              size="small"
                              onClick={() => window.open(referralHref, '_blank')}
                            >
                              <Iconify
                                icon="eva:external-link-outline"
                                sx={{ width: 18, height: 18 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue ?? outputRef ?? ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  if (isOtherResourceRef) {
                    const resourceHref = `${fhirBaseUrl}/${refType}/${refId}`;
                    return (
                      <Stack
                        key={`output-bbox-${index}`}
                        direction="column"
                        mt={2}
                        spacing={1}
                        sx={outputStackSx}
                      >
                        <Badge
                          badgeContent={`Output ${index + 1}`}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{
                            '& .MuiBadge-badge': {
                              transform: 'scale(1) translate(0%, -80%)',
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Type
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {valueType ?? refType}
                          </Typography>
                          <Tooltip title="View resource">
                            <IconButton
                              size="small"
                              onClick={() => window.open(resourceHref, '_blank')}
                            >
                              <Iconify
                                icon="eva:external-link-outline"
                                sx={{ width: 18, height: 18 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Typography sx={[valueTextFontSize, { flex: 0.4, color: '#637381' }]}>
                            Value
                          </Typography>
                          <Typography sx={[valueTextFontSize, blackTextColor, { flex: 1 }]}>
                            {displayValue ?? outputRef ?? ''}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  return (
                    <BadgeBox
                      key={`output-bbox-${index}`}
                      index={index}
                      items={itemsBadge}
                      blackTextColor={blackTextColor}
                      valueTextFontSize={valueTextFontSize}
                      titleBadge={'Output'}
                    />
                  );
                })}
              </>
            )}
          </SectionContainer>

          {/* Service Request Details Section */}
          {basedOnServiceRequestId && (
            <SectionContainer>
              <ServiceRequestDetailsContainer serviceRequestId={basedOnServiceRequestId} />
            </SectionContainer>
          )}

          {/* Annotations Section (preview) — collapsed by default, expand to see list */}
          <Box sx={{ mb: 2 }}>
            <ExpandableCard
              title={`Annotations (${(() => {
                const ann = taskToAnnotations(task, currentUserName);
                return ann.length;
              })()})`}
              sxProp={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': {
                  borderColor: 'primary.lighter',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
              }}
            >
              {(() => {
                const annotations = taskToAnnotations(task, currentUserName);
                const count = annotations.length;
                const preview = annotations.slice(0, ANNOTATION_PREVIEW_LIMIT);
                const truncateLen = ANNOTATION_TRUNCATE_LENGTH;
                const truncate = (t: string) =>
                  !t ? '' : t.length <= truncateLen ? t : `${t.slice(0, truncateLen)}…`;
                return (
                  <Box sx={{ pt: 2, pb: 1.5, px: 0 }}>
                    {count === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No annotations yet
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {preview.map((a, idx) => (
                          <Card
                            key={`${a.authorString}-${a.time}-${idx}`}
                            elevation={0}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            }}
                          >
                            <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                  mb: 0.75,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                  {a.authorString || currentUserName || '—'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                                  {getAnnotationFormattedDate(a.time || '')}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.45 }}>
                                {truncate(a.text)}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => setIsAnnotationsPanelOpen(true)}
                        sx={{ cursor: 'pointer', fontWeight: 500 }}
                      >
                        View All
                      </Link>
                    </Box>
                  </Box>
                );
              })()}
            </ExpandableCard>
          </Box>

          <TaskAnnotationsPanel
            open={isAnnotationsPanelOpen}
            onClose={() => setIsAnnotationsPanelOpen(false)}
            task={task}
            taskId={taskIdProp ?? (task as any)?.id}
            onSuccesfulEdit={(response) => {
              onSuccesfulEdit(response);
              setIsAnnotationsPanelOpen(false);
            }}
          />

          <Box sx={{ mt: 2, px: 2.8 }}>
            <Button
              disabled={
                verifiedNatTaskBoolean
                  ? !checkAclValidation({ acls: [crsAcls.NAT.TASK.EDIT] })
                  : !isAllowedToEdit
              }
              sx={{
                height: 48,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                },
              }}
              fullWidth
              onClick={() => {
                onEdit(task as WrappedTask);
              }}
              variant="contained"
            >
              Edit
            </Button>
          </Box>

          {/* Document / attachment preview dialog */}
          <Dialog
            fullWidth
            maxWidth="md"
            open={!!previewDocumentId}
            onClose={() => setPreviewDocumentId(null)}
          >
            <DialogCloseButton onClose={() => setPreviewDocumentId(null)} />
            <DialogTitle>{i18n('selectAttachment.previewDocument', 'crs')}</DialogTitle>
            {isPreviewDocumentLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : previewDocument ? (
              <Viewer
                documentReference={previewDocument}
                typeResource="documentReference"
                patientId={task?.getPatientId?.()}
              />
            ) : null}
            <DialogActions sx={{ pt: 0 }}>
              <Button onClick={() => setPreviewDocumentId(null)} variant="outlined" color="inherit">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* QuestionnaireResponse preview dialog */}
          <Dialog
            fullWidth
            maxWidth="sm"
            open={!!previewQuestionnaireResponseId}
            onClose={() => setPreviewQuestionnaireResponseId(null)}
          >
            <DialogCloseButton onClose={() => setPreviewQuestionnaireResponseId(null)} />
            <DialogTitle>Questionnaire Response</DialogTitle>
            <DialogContent>
              {isPreviewQRLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : previewQuestionnaireResponse ? (
                <QuestionnaireResponsePreviewContent
                  qr={previewQuestionnaireResponse as QuestionnaireResponse}
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setPreviewQuestionnaireResponseId(null)}
                variant="outlined"
                color="inherit"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Box flex={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <CircularProgress />
        </Box>
      )}
    </CustomDrawer>
  );
};

export default TaskDetailDrawer;
